<template>
  <div>
    <list-spot-trackers-action :loading="loading" :spotTrackers="spotTrackers" @import="submit" />
    <o-message :errorMessages="importErrorMessages" title="Erreurs d'importation" />
    <list-spot-trackers-filter />
    <list-spot-trackers-table :spotTrackers="spotTrackers" :loading="loading" class="box" />
  </div>
</template>

<script>
import OMessage from '@components/Message.vue'
import ListSpotTrackersTable from './ListSpotTrackersTable.vue'
import ListSpotTrackersFilter from './ListSpotTrackersFilter.vue'
import ListSpotTrackersAction from './ListSpotTrackersAction.vue'
import { getErrorMessages, importOwnerSpotTrackerError } from '@helpers/importErrorMessages'
import { mapActions } from 'vuex'

export default {
  name: 'ListSpotTrackers',

  components: {
    ListSpotTrackersAction,
    ListSpotTrackersFilter,
    ListSpotTrackersTable,
    OMessage,
  },

  data() {
    return {
      loading: true,
      spotTrackers: [],
      importErrorMessages: [],
    }
  },

  async beforeRouteUpdate(to, from, next) {
    const filters = this.buildFiltersObject(to.query)

    await this.fetchSpotTrackers(filters)
    await this.fetchLatestSpotTrackersLocation(filters)
    next()
  },

  async mounted() {
    const filters = this.buildFiltersObject(this.$route.query)
    await this.fetchSpotTrackers(filters)
    await this.fetchLatestSpotTrackersLocation(filters)
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    buildFiltersObject(routeQuery) {
      return {
        keyword: routeQuery?.keyword ?? null,
        availability: routeQuery?.availability ?? null,
        owner: routeQuery?.owner ?? null,
        expired: routeQuery?.expired ?? null,
        wear: routeQuery?.wear ?? null,
        model: routeQuery?.model ?? null,
      }
    },

    async fetchSpotTrackers(filters) {
      this.loading = true
      this.spotTrackers = await this.$services.spotTrackerService.getAll(filters)
      this.loading = false
    },

    async fetchLatestSpotTrackersLocation(filters) {
      const latestLocations =
        await this.$services.spotTrackerLocationService.getLatestLocationBySpotTrackersFilters(
          filters,
        )

      // We need to rebuild the trackers objetcs to make them easier to use in the table
      // With this, we can use them directly, and do not need to do the matching in the template.
      const locationsMap = new Map(
        latestLocations.map((location) => [location.spotTrackerId, location]),
      )

      this.spotTrackers = this.spotTrackers.map((tracker) => {
        const location = locationsMap.get(tracker.id)
        return {
          ...tracker,
          locatedAt: location?.locatedAt,
          batteryPercentage: location?.batteryLevel,
        }
      })
    },

    async submit({ result, error }) {
      if (error) {
        this.importErrorMessages = [error]

        return
      }

      this.importErrorMessages = []
      this.loading = true

      try {
        const updatedSpotTrackers = await this.$services.spotTrackerService.updateAll({
          spotTrackers: this.formatSpotTrackersData(result),
        })

        this.updateSpotTrackersList(updatedSpotTrackers)

        this.addToastMessage({
          text: 'Les propriétaires des spots ont été modifiés',
          type: 'is-success',
        })
      } catch (err) {
        this.importErrorMessages = getErrorMessages(err, importOwnerSpotTrackerError)
      }

      this.loading = false
    },

    formatSpotTrackersData(data) {
      return data.map((spotTracker) => ({
        ...spotTracker,
        owner: spotTracker.owner.toLowerCase(),
      }))
    },

    updateSpotTrackersList(updatedSpotTrackers) {
      this.spotTrackers = [
        ...updatedSpotTrackers,
        ...this.spotTrackers.filter((spot) => {
          const ids = new Set(updatedSpotTrackers.map((s) => s.id))
          return !ids.has(spot.id)
        }),
      ]
    },
  },
}
</script>
